import React, { useEffect, useState } from "react";
import { useAuth } from "../utils/useAuth";
import { db } from "../firebase";
import { createManagerNotification, createCoachNotification } from "../utils/notifications";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";

export default function RefereeDashboard() {
  const { user, profile } = useAuth();
  const [games, setGames] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  
  // Collapsible sections state
  const [pendingOpen, setPendingOpen] = useState(true);
  const [upcomingOpen, setUpcomingOpen] = useState(true);
  const [completedOpen, setCompletedOpen] = useState(false);

  // Load ALL games where this referee has any role
  useEffect(() => {
    if (!user) return;

    const unsub = onSnapshot(collection(db, "games"), (snap) => {
      const allGames = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      
      // Filter games where this referee has ANY role
      const myGames = allGames.filter(g => 
        g.assignedRefereeId === user.uid ||
        g.assistantReferee1Id === user.uid ||
        g.assistantReferee2Id === user.uid ||
        g.assistant4Id === user.uid ||
        g.assistant5Id === user.uid ||
        g.timeKeeperId === user.uid ||
        g.playerMovementRecorderId === user.uid ||
        g.statisticianId === user.uid ||
        g.technicalZoneManagerId === user.uid
      );
      
      // Filter out games that have been unassigned (status = pending with no referee)
      const activeGames = myGames.filter(g => 
        g.status !== "pending" && 
        (g.assignedRefereeId || g.assistantReferee1Id || g.assistantReferee2Id ||
         g.assistant4Id || g.assistant5Id || g.timeKeeperId || 
         g.playerMovementRecorderId || g.statisticianId || g.technicalZoneManagerId)
      );
      
      setGames(activeGames);
    });

    return unsub;
  }, [user]);

  // Load notifications
  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, "notifications"),
      where("recipientId", "==", user.uid)
    );

    const unsub = onSnapshot(q, (snap) => {
      const notifs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Sort by newest first, unread first
      notifs.sort((a, b) => {
        if (a.read !== b.read) return a.read ? 1 : -1;
        return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
      });
      setNotifications(notifs);
    });

    return unsub;
  }, [user]);

  async function markAsRead(notificationId) {
    await updateDoc(doc(db, "notifications", notificationId), {
      read: true,
    });
  }

  async function deleteNotification(notificationId) {
    await updateDoc(doc(db, "notifications", notificationId), {
      read: true,
    });
  }

  async function acceptGame(id) {
    const game = games.find(g => g.id === id);
    
    await updateDoc(doc(db, "games", id), {
      refereeStatus: "accepted",
      status: "scheduled",
      refereeResponseAt: new Date(),
      refereeAccepted: true,
    });
    
    // Notify coach
    if (game && game.coachId) {
      await createCoachNotification(
        game.coachId,
        "Referee Accepted Assignment",
        `${profile.fullName} has accepted to officiate your game: ${game.teamA} vs ${game.teamB} on ${game.date} at ${game.time}.`
      );
    }
    
    alert("You have accepted the game.");
  }

  async function declineGame(id) {
    const game = games.find(g => g.id === id);
    
    // Determine which role this user is declining from
    const updates = {
      refereeResponseAt: new Date(),
      refereeAccepted: false,
      declinedBy: profile.fullName,
      declinedAt: new Date()
    };
    
    // Clear the specific role assignment
    if (game.assignedRefereeId === user.uid) {
      updates.assignedRefereeId = null;
      updates.assignedRefereeName = null;
      updates.assignedRefereeLeague = null;
      updates.refereeStatus = null;
    }
    
    if (game.assistantReferee1Id === user.uid) {
      updates.assistantReferee1Id = null;
      updates.assistantReferee1Name = null;
      updates.assistantReferee1League = null;
    }
    
    if (game.assistantReferee2Id === user.uid) {
      updates.assistantReferee2Id = null;
      updates.assistantReferee2Name = null;
      updates.assistantReferee2League = null;
    }
    
    if (game.assistant4Id === user.uid) {
      updates.assistant4Id = null;
      updates.assistant4Name = null;
    }
    
    if (game.assistant5Id === user.uid) {
      updates.assistant5Id = null;
      updates.assistant5Name = null;
    }
    
    if (game.timeKeeperId === user.uid) {
      updates.timeKeeperId = null;
      updates.timeKeeperName = null;
    }
    
    if (game.playerMovementRecorderId === user.uid) {
      updates.playerMovementRecorderId = null;
      updates.playerMovementRecorderName = null;
    }
    
    if (game.statisticianId === user.uid) {
      updates.statisticianId = null;
      updates.statisticianName = null;
    }
    
    if (game.technicalZoneManagerId === user.uid) {
      updates.technicalZoneManagerId = null;
      updates.technicalZoneManagerName = null;
    }
    
    // Check if this was the main referee - if so, move back to pending
    // Otherwise keep current status (assigned or scheduled)
    if (game.assignedRefereeId === user.uid) {
      updates.status = "pending";
    }
    
    // Move game back to pending and clear referee assignments
    await updateDoc(doc(db, "games", id), updates);
    
    // Try to send notification to manager (don't block on failure)
    if (game) {
      try {
        const roleName = game.assignedRefereeId === user.uid ? "Referee"
          : game.assistantReferee1Id === user.uid ? "Assistant Referee 1"
          : game.assistantReferee2Id === user.uid ? "Assistant Referee 2"
          : game.assistant4Id === user.uid ? "Assistant 4"
          : game.assistant5Id === user.uid ? "Assistant 5"
          : game.timeKeeperId === user.uid ? "Time Keeper"
          : game.playerMovementRecorderId === user.uid ? "Player Movement Recorder"
          : game.statisticianId === user.uid ? "Statistician"
          : game.technicalZoneManagerId === user.uid ? "Technical Zone Manager"
          : "Official";
        
        await createManagerNotification(
          `${roleName} Declined Assignment`,
          `${profile.fullName} has declined the ${roleName} assignment for ${game.teamA} vs ${game.teamB} on ${game.date} at ${game.time}.${updates.status === "pending" ? " The game has been moved back to pending." : ""}`,
          game.union || profile.union
        );
      } catch (err) {
        console.error("Failed to send notification (non-critical):", err);
        // Don't show error to user - decline action was successful
      }
    }
    
    alert(`You declined this game.${updates.status === "pending" ? " It has been moved back to pending." : ""}`);
  }

  async function markGameComplete(id, score) {
    await updateDoc(doc(db, "games", id), {
      status: "completed",
      completedAt: new Date(),
      finalScore: score,
    });
    alert("Game marked as completed!");
  }

  // Check if game is more than 36 hours away
  function canDeclineGame(gameDate, gameTime) {
    if (!gameDate || !gameTime) return false;
    
    const gameDateTime = new Date(`${gameDate} ${gameTime}`);
    const now = new Date();
    const hoursUntilGame = (gameDateTime - now) / (1000 * 60 * 60);
    
    return hoursUntilGame > 36;
  }

  // Check if game ended more than 30 minutes ago
  function canMarkComplete(gameDate, gameTime) {
    if (!gameDate || !gameTime) return false;
    
    const gameDateTime = new Date(`${gameDate} ${gameTime}`);
    const now = new Date();
    const minutesSinceGame = (now - gameDateTime) / (1000 * 60);
    
    return minutesSinceGame > 30;
  }

  // Separate games into pending, upcoming, and completed
  const pendingGames = games.filter(g => g.refereeStatus !== "accepted" && g.status !== "completed");
  const upcomingGames = games.filter(g => g.refereeStatus === "accepted" && g.status !== "completed");
  const completedGames = games.filter(g => g.status === "completed");

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">My Referee Appointments</h2>

      {/* NOTIFICATIONS */}
      {notifications.filter(n => !n.read).length > 0 && (
        <div className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              Notifications
              <span className="px-2 py-1 bg-red-600 text-white text-xs rounded-full">
                {notifications.filter(n => !n.read).length}
              </span>
            </h3>
            <button
              onClick={() => {
                notifications.filter(n => !n.read).forEach(n => markAsRead(n.id));
              }}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Dismiss All
            </button>
          </div>
          <div className="relative" style={{ paddingBottom: `${(notifications.filter(n => !n.read).length - 1) * 8}px` }}>
            {notifications.filter(n => !n.read).map((notif, index) => (
              <div
                key={notif.id}
                className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded shadow-lg hover:shadow-xl transition-all"
                style={{
                  position: index === 0 ? 'relative' : 'absolute',
                  top: index === 0 ? 0 : `${index * 10}px`,
                  left: 0,
                  right: 0,
                  zIndex: notifications.filter(n => !n.read).length - index,
                  transform: `translateY(${index * 8}px)`,
                  opacity: 1 - (index * 0.15),
                }}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-semibold text-blue-900">{notif.title}</p>
                    <p className="text-sm text-gray-700 mt-1">{notif.message}</p>
                    {notif.createdAt && (
                      <p className="text-xs text-gray-500 mt-2">
                        {new Date(notif.createdAt.seconds * 1000).toLocaleString()}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => markAsRead(notif.id)}
                    className="ml-4 text-sm px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* THREE COLUMN LAYOUT - Collapsible */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: PENDING ASSIGNMENTS */}
        <div>
          <div 
            className="bg-yellow-600 text-white p-4 rounded-t-lg mb-4 flex items-center justify-between cursor-pointer hover:bg-yellow-700"
            onClick={() => setPendingOpen(!pendingOpen)}
          >
            <h3 className="text-xl font-bold">⏳ Pending Assignments</h3>
            <div className="flex items-center gap-2">
              <span className="bg-white text-yellow-600 px-3 py-1 rounded-full font-bold text-sm">
                {pendingGames.length}
              </span>
              <svg 
                className={`w-5 h-5 transition-transform ${pendingOpen ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          {pendingOpen && (
            <>
              {pendingGames.length === 0 ? (
                <p className="text-gray-500">No pending assignments.</p>
              ) : (
                <div className="space-y-4">
              {pendingGames.map((g) => (
                <div
                  key={g.id}
                  className="bg-white p-4 rounded shadow border-l-4 border-yellow-500"
                >
                  <h3 className="text-xl font-semibold">
                    {g.teamA} vs {g.teamB}
                  </h3>
                  <p className="text-gray-600">
                    {g.date} @ {g.time} — {g.venue} (Field {g.field})
                  </p>
                  <p className="text-gray-600 mt-1">
                    Age: {g.ageGroup} • Comp: {g.competition}
                  </p>

                  <p className="mt-2">
                    Status:{" "}
                    <span className="font-semibold text-yellow-700">
                      {g.refereeStatus?.toUpperCase() || "PENDING"}
                    </span>
                  </p>

                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={() => setSelectedGame(g)}
                      className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-800"
                    >
                      View Details
                    </button>

                    {g.refereeStatus !== "declined" && (
                      <>
                        <button
                          onClick={() => acceptGame(g.id)}
                          className="px-3 py-1 bg-green-700 text-white rounded hover:bg-green-800"
                        >
                          Accept
                        </button>

                        <button
                          onClick={() => declineGame(g.id)}
                          className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                        >
                          Decline
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
              )}
            </>
          )}
        </div>

        {/* MIDDLE: UPCOMING GAMES */}
        <div>
          <div 
            className="bg-green-600 text-white p-4 rounded-t-lg mb-4 flex items-center justify-between cursor-pointer hover:bg-green-700"
            onClick={() => setUpcomingOpen(!upcomingOpen)}
          >
            <h3 className="text-xl font-bold">📅 Upcoming Games</h3>
            <div className="flex items-center gap-2">
              <span className="bg-white text-green-600 px-3 py-1 rounded-full font-bold text-sm">
                {upcomingGames.length}
              </span>
              <svg 
                className={`w-5 h-5 transition-transform ${upcomingOpen ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          {upcomingOpen && (
            <>
              {upcomingGames.length === 0 ? (
                <p className="text-gray-500">No upcoming games yet.</p>
              ) : (
                <div className="space-y-4">
              {upcomingGames.map((g) => {
                const canComplete = canMarkComplete(g.date, g.time);
                
                return (
                  <div
                    key={g.id}
                    className="bg-white p-4 rounded shadow border-l-4 border-green-600"
                  >
                    <h3 className="text-xl font-semibold">
                      {g.teamA} vs {g.teamB}
                    </h3>
                    <p className="text-gray-600">
                      {g.date} @ {g.time} — {g.venue} (Field {g.field})
                    </p>
                    <p className="text-gray-600 mt-1">
                      Age: {g.ageGroup} • Comp: {g.competition}
                    </p>

                    <p className="mt-2">
                      Status:{" "}
                      <span className="font-semibold text-green-700">
                        ✓ ACCEPTED
                      </span>
                    </p>

                    <div className="flex gap-3 mt-4">
                      <button
                        onClick={() => setSelectedGame(g)}
                        className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-800"
                      >
                        View Details
                      </button>
                      
                      <button
                        onClick={() => declineGame(g.id)}
                        disabled={!canDeclineGame(g.date, g.time)}
                        className={`px-3 py-1 rounded ${
                          canDeclineGame(g.date, g.time)
                            ? "bg-red-600 text-white hover:bg-red-700"
                            : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                        title={!canDeclineGame(g.date, g.time) ? "Cannot decline within 36 hours of game time" : "Decline this game"}
                      >
                        Decline
                      </button>

                      <button
                        onClick={() => {
                          const teamA = prompt(`Enter ${g.teamA} score:`);
                          const teamB = prompt(`Enter ${g.teamB} score:`);
                          if (teamA !== null && teamB !== null && !isNaN(teamA) && !isNaN(teamB)) {
                            markGameComplete(g.id, `${teamA}-${teamB}`);
                          } else if (teamA !== null || teamB !== null) {
                            alert("Please enter valid numeric scores");
                          }
                        }}
                        disabled={!canComplete}
                        className={`px-3 py-1 rounded ${
                          canComplete
                            ? "bg-blue-600 text-white hover:bg-blue-700"
                            : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                        title={!canComplete ? "Available 30 minutes after game time" : "Mark as completed"}
                      >
                        Completed
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
              )}
            </>
          )}
        </div>

        {/* RIGHT: COMPLETED GAMES */}
        <div>
          <div 
            className="bg-gray-700 text-white p-4 rounded-t-lg mb-4 flex items-center justify-between cursor-pointer hover:bg-gray-800"
            onClick={() => setCompletedOpen(!completedOpen)}
          >
            <h3 className="text-xl font-bold">✓ Completed Games</h3>
            <div className="flex items-center gap-2">
              <span className="bg-white text-gray-700 px-3 py-1 rounded-full font-bold text-sm">
                {completedGames.length}
              </span>
              <svg 
                className={`w-5 h-5 transition-transform ${completedOpen ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          {completedOpen && (
            <>
              {completedGames.length === 0 ? (
                <p className="text-gray-500">No completed games yet.</p>
              ) : (
                <div className="space-y-4">
              {completedGames.map((g) => (
                <div
                  key={g.id}
                  className="bg-white p-4 rounded shadow border-l-4 border-gray-500"
                >
                  <h3 className="text-xl font-semibold">
                    {g.teamA} vs {g.teamB}
                  </h3>
                  <p className="text-gray-600">
                    {g.date} @ {g.time} — {g.venue} (Field {g.field})
                  </p>
                  <p className="text-gray-600 mt-1">
                    Age: {g.ageGroup} • Comp: {g.competition}
                  </p>

                  {g.finalScore && (
                    <div className="mt-3 p-2 bg-gray-100 rounded">
                      <p className="text-lg font-bold text-center">
                        Final Score: {g.finalScore}
                      </p>
                    </div>
                  )}

                  <p className="mt-2">
                    Status:{" "}
                    <span className="font-semibold text-gray-700">
                      ✓ COMPLETED
                    </span>
                  </p>

                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={() => setSelectedGame(g)}
                      className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-800"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* GAME DETAILS MODAL */}
      {selectedGame && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSelectedGame(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-2xl font-bold">Game Details</h2>
              <button
                onClick={() => setSelectedGame(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <h3 className="text-xl font-semibold">
                  {selectedGame.teamA} {selectedGame.teamAGrade && `(${selectedGame.teamAGrade})`} vs {selectedGame.teamB} {selectedGame.teamBGrade && `(${selectedGame.teamBGrade})`}
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 font-semibold">Date</p>
                  <p>{selectedGame.date}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-semibold">Time</p>
                  <p>{selectedGame.time}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-semibold">Venue</p>
                  <p>{selectedGame.venue}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-semibold">Field</p>
                  <p>{selectedGame.field}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-semibold">Age Group</p>
                  <p>{selectedGame.ageGroup}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-semibold">Competition</p>
                  <p>{selectedGame.competition}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-semibold">Coach/Organiser</p>
                  <p>{selectedGame.coachName || "N/A"}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-semibold">Status</p>
                  <p className="font-semibold">{selectedGame.status?.toUpperCase()}</p>
                </div>
              </div>

              {selectedGame.needsReferee && (
                <div className="mt-3">
                  <p className="text-sm text-gray-600 font-semibold">Needs Referee</p>
                  <p>✓ Yes</p>
                </div>
              )}

              {selectedGame.needsAssistantReferee && (
                <div className="mt-3">
                  <p className="text-sm text-gray-600 font-semibold">Needs Assistant Referee</p>
                  <p>✓ Yes</p>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setSelectedGame(null)}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Return to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
